import type { ApplicationLogMetadata } from '@/lib/types';
import type { Event } from '@sentry/nextjs';

/**
 * Clean stack trace to remove internal Node.js and Next.js frames
 */
export function cleanStackTrace(stack: string): string {
  return stack
    .split('\n')
    .filter(line => {
      // Keep the error message
      if (!line.trim().startsWith('at ')) return true;
      
      // Filter out internal Node.js modules
      if (line.includes('(node:')) return false;
      if (line.includes('at node:')) return false;
      
      // Filter out Next.js/React internals that are noise
      if (line.includes('node_modules/next/')) return false;
      if (line.includes('node_modules/react/')) return false;
      if (line.includes('node_modules/react-dom/')) return false;
      if (line.includes('node_modules/next/dist/')) return false;
      
      return true;
    })
    .join('\n');
}

// These exact identifiers carry tracing/routing data, not credentials; the
// Forms `sourceKey` is how a submission is traced across services. Keep the
// allowlist narrow: every other *Key (and every *Secret/*Token) is sensitive.
// Keys are compared lower-cased with `-` and `_` removed (`api_key` = `apikey`).
const tracingKeys = new Set(['idempotencykey', 'requestkey', 'formkey', 'cachekey', 'primarykey', 'sortkey',
  'sourcekey', 'oldsourcekey', 'newsourcekey', 'scopecachekey']);
const sensitiveKeys = new Set(['credentials', 'auth', 'bearer', 'dsn']);
const sensitiveSuffix = /(?:secret|token|key|pass|passwd|password|passphrase|hash|cookie|authorization)$/;

// Credential-bearing URLs (M3U links carry `user:pass@` or `username=&password=`)
// are redacted wherever they appear in a string value, whatever the key. The
// userinfo match runs to the last `@` before `/`, `\\`, `?`, `#`, whitespace or
// a double quote. Empty users and embedded `@` are covered; ambiguous raw `/`
// passwords are not. Double quotes bound URLs inside JSON text. It accepts
// `\\` for `//` as browsers do. Fragments are read like queries.
const urlUserinfo = /([/\\]{2})[^/\\\s?#"]*@/g;
// A name cannot cross another separator, so failed matches scan each name once.
const queryParam = /([?&#])([^=?&#\r\n"']+)=([^&#\s"']*)/g;
const credentialParams = new Set(['username', 'user', 'secret', 'token', 'key', 'pass', 'passwd',
  'password', 'passphrase', 'hash', 'cookie', 'authorization', 'signature', 'sig',
  'apikey', 'accesstoken', 'refreshtoken', 'clientsecret']);
// Ordinary words that end in `key`. Every other glued `…key` stays redacted as
// in #264: an allowlist of credential prefixes can never be complete.
const innocentKeyWords = new Set(['monkey', 'turkey', 'donkey', 'hockey', 'jockey', 'whiskey', 'lackey']);
// Error messages sent to Sentry also carry credentials outside URLs: an
// `Authorization` value, a JSON body echoed into a message, or `name=value` after
// a space. Names are bounded, so failed matches stay linear. Log storage keeps
// using scrubText alone (#238: message text in logs is the caller's).
// After an `Authorization` label, a scheme (any case) marks its value as a
// credential; without one, only a token-shaped value is, so `Authorization:
// denied` stays readable. A bare `Bearer`/`Basic` in prose needs a token shape.
const authorizationValue = /\b(authorization["']?\s*[:=]\s*["']?)(?:((?:bearer|basic|token|digest)\s+)[^\s"',;]+|(?=[A-Za-z]*[0-9._~+/=-])[A-Za-z0-9._~+/=-]{8,})/gi;
const bearerToken = /\b((?:[Bb]earer|BEARER|[Bb]asic|BASIC)\s+)(?=[A-Za-z]*[0-9._~+/=-])[A-Za-z0-9._~+/=-]{6,}/g;
const jsonStringField = /("([^"\\\n]{1,100})"\s*:\s*)"(?:[^"\\\n]|\\.)*"/g;
// The same field inside a JSON string, escaped: \"access_token\":\"…\".
const escapedJsonField = /(\\"([^"\\\n]{1,100})\\"\s*:\s*)\\"(?:[^"\\\n]|\\[^"\n])*\\"/g;
// In prose, generic names (`Idempotency key=…`, `hash=…`) are not credentials.
const genericProseNames = new Set(['key', 'hash', 'user', 'username']);
const bareParam = /(^|[\s,;(])([A-Za-z][\w.-]{0,63})=("(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*'|[^\s&#"',;)]*)/g;
const stackFrameStart = /^(?:\s+at |[^@\n]*@[a-z][\w+.-]*:\/\/)/i;
const stackLocation = /:\d+:\d+\)?\s*$/;

// A Setup capability sits in a URL path, where no parameter name marks it as private.
const setupCapability = /vps\d+(?:\.|%2e)capk_[^\s"'<>&?#\\]+/gi;

function decodeParamName(name: string): string {
  try { return decodeURIComponent(name.replace(/\+/g, ' ')); } catch { return name; }
}

/** Redact credentials inside free text (URLs with userinfo or credential params). */
export function scrubText(value: string): string {
  return value.split('\n').map(line => {
    // Locations belong to the frame, not to the last query value. Firefox has
    // no message header: its first line is scrubbed just like every V8 frame.
    const location = stackFrameStart.test(line) ? stackLocation.exec(line)?.[0] || '' : '';
    const text = location ? line.slice(0, -location.length) : line;
    return text
      .replace(setupCapability, '[redacted]')
      .replace(urlUserinfo, '$1[redacted]@')
      .replace(queryParam, (match, separator: string, name: string) =>
        isSensitiveParam(name) ? `${separator}${name}=[redacted]` : match) + location;
  }).join('\n');
}

/** Error text bound for Sentry: scrubText plus bearer values, JSON fields and bare `name=value`. */
export function scrubEventText(value: string): string {
  return scrubText(value)
    .replace(authorizationValue, (_match, label: string, scheme = '') => `${label}${scheme}[redacted]`)
    .replace(bearerToken, '$1[redacted]')
    .replace(jsonStringField, (match, prefix: string, name: string) =>
      isSensitiveParam(name) ? `${prefix}"[redacted]"` : match)
    .replace(escapedJsonField, (match, prefix: string, name: string) =>
      isSensitiveParam(name) ? `${prefix}\\"[redacted]\\"` : match)
    .replace(bareParam, (match, lead: string, name: string) =>
      isSensitiveParam(name) && !genericProseNames.has(name.toLowerCase()) ? `${lead}${name}=[redacted]` : match);
}

function isSensitiveParam(name: string): boolean {
  const decoded = decodeParamName(name);
  const normalized = decoded.toLowerCase().replace(/[-_.+\s[\]]/g, '');
  // Preserve #264's suffix redaction for glued names, except ordinary words
  // such as `monkey`; OAuth `code` stays unchanged.
  const lastWord = decoded.replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .split(/[-_.+\s[\]]/).filter(Boolean).pop()?.toLowerCase() || '';
  const sensitiveEnding = sensitiveSuffix.test(normalized) && !innocentKeyWords.has(normalized);
  return !tracingKeys.has(normalized) && (sensitiveKeys.has(normalized) || sensitiveEnding ||
    credentialParams.has(normalized) || credentialParams.has(lastWord));
}

/** Scrub Sentry event text, request fields, breadcrumbs and trace data without mutating the input. */
export function scrubSentryRequest<T extends Event>(event: T): T {
  const request = event.request;
  const query = request?.query_string;
  const scrubEntry = ([name, value]: [string, string]): [string, string] =>
    [name, isSensitiveParam(name) ? '[redacted]' : scrubText(String(value))];
  // The SDK always sends arrays; anything else passes through rather than
  // throwing, because a throwing hook makes Sentry drop the whole event.
  const each = <V>(list: V[] | undefined, map: (item: V & object) => V): V[] | undefined =>
    Array.isArray(list) ? list.map(item => item && typeof item === 'object' ? map(item) : item) : list;
  const text = (value: unknown) => typeof value === 'string' ? scrubEventText(value) : value;
  return { ...event,
    ...(typeof event.message === 'string' && { message: scrubEventText(event.message) }),
    ...(event.logentry && { logentry: { ...event.logentry,
      message: text(event.logentry.message) as string | undefined,
      // A templated message carries its substituted values separately.
      ...(Array.isArray(event.logentry.params) && { params: event.logentry.params.map(param =>
        param && typeof param === 'object' ? eventTextDeep(scrubMetadata(param)) : text(param)) }),
    } }),
    ...(event.exception?.values && { exception: { ...event.exception,
      values: each(event.exception.values, exception => ({ ...exception,
        value: text(exception.value) as string | undefined,
      })),
    } }),
    // Non-Error throwables are serialized here (`__serialized__.message`), and
    // the loggers attach metadata here: its strings are event text too.
    ...(event.extra && { extra: eventTextDeep(scrubMetadata(event.extra)) as typeof event.extra }),
    ...(typeof event.transaction === 'string' && { transaction: scrubText(event.transaction) }),
    ...(event.contexts?.trace && { contexts: { ...event.contexts, trace: {
      ...event.contexts.trace, data: scrubMetadata(event.contexts.trace.data),
    } } }),
    ...(request && { request: {
      ...request,
      url: typeof request.url === 'string' ? scrubText(request.url) : request.url,
      headers: scrubMetadata(request.headers),
      data: scrubBody(request.data, scrubEntry),
      // Cookie names are arbitrary; every cookie value can hold a session.
      cookies: request.cookies ? Object.fromEntries(Object.keys(request.cookies).map(name => [name, '[redacted]'])) : undefined,
      query_string: typeof query === 'string' ? scrubText(`?${query}`).slice(1)
        : Array.isArray(query) ? query.map(scrubEntry)
          : query ? Object.fromEntries(Object.entries(query).map(scrubEntry)) : query,
    } }),
    ...(event.breadcrumbs && { breadcrumbs: each(event.breadcrumbs, breadcrumb => ({
      ...breadcrumb,
      message: text(breadcrumb.message) as string | undefined,
      data: scrubMetadata(breadcrumb.data),
    })) }),
    // Outgoing HTTP spans carry the full URL in their description and data.
    ...(event.spans && { spans: each(event.spans, span => ({
      ...span,
      description: text(span.description) as string | undefined,
      data: scrubMetadata(span.data) as typeof span.data,
    })) }),
  };
}

/** Apply scrubEventText to every string in an already-copied metadata tree. */
function eventTextDeep(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') return scrubEventText(value);
  if (!value || typeof value !== 'object' || depth > 20) return value;
  if (Array.isArray(value)) return value.map(item => eventTextDeep(item, depth + 1));
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, eventTextDeep(item, depth + 1)]));
}

/** A request body may arrive as JSON text, a form-encoded string or entry tuples. */
function scrubBody(data: unknown, scrubEntry: (entry: [string, string]) => [string, string]): unknown {
  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data);
      if (parsed && typeof parsed === 'object') return JSON.stringify(scrubMetadata(parsed));
    } catch { /* not JSON: read it as a form body */ }
    return scrubText(`?${data}`).slice(1);
  }
  if (Array.isArray(data) && data.every(entry => Array.isArray(entry) && entry.length === 2 && typeof entry[0] === 'string')) {
    return data.map(entry => scrubEntry(entry as [string, string]));
  }
  return scrubMetadata(data);
}

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[-_]/g, '');
  return !tracingKeys.has(normalized) && (sensitiveKeys.has(normalized) || sensitiveSuffix.test(normalized));
}

/** Scrub structured metadata without changing Error messages or the input. */
export function scrubMetadata(metadata: ApplicationLogMetadata | undefined): ApplicationLogMetadata | undefined;
export function scrubMetadata(metadata: Record<string, unknown> | undefined): Record<string, unknown> | undefined;
export function scrubMetadata(metadata: unknown): unknown;
export function scrubMetadata(metadata: unknown): unknown {
  // A value that throws while being read (a getter, toJSON) is dropped rather
  // than logged raw, and never turns a log call into a failure.
  try {
    return scrubValue(metadata);
  } catch {
    return { metadataScrubFailed: true };
  }
}

function scrubValue(metadata: unknown): unknown {
  const ancestors = new WeakSet<object>();

  function scrub(value: unknown): unknown {
    if (typeof value === 'string') return scrubText(value);
    if (!value || typeof value !== 'object') return value;
    if (value instanceof Date) return value;
    if (ancestors.has(value)) return '[circular]';
    ancestors.add(value);
    let result: unknown;
    if (Array.isArray(value)) {
      result = value.map(scrub);
    } else if (value instanceof Error) {
      // A live Error's message is scrubbed like any other text. Sentry event text, and `extra`
      // (normalized to plain objects before beforeSend), is scrubbed by scrubSentryRequest.
      // Everything structured, including `cause`, is scrubbed.
      const structured = scrubObject({ ...value, name: value.name, stack: value.stack, cause: value.cause });
      result = { ...structured, message: scrubText(value.message) };
    } else if (value instanceof Map || (typeof Headers !== 'undefined' && value instanceof Headers)) {
      result = scrubObject(Object.fromEntries(value.entries()));
    } else if (typeof (value as { toJSON?: unknown }).toJSON === 'function') {
      // URL, Prisma Decimal and similar: log what JSON.stringify would store.
      result = scrub((value as { toJSON: () => unknown }).toJSON());
    } else {
      // Plain objects and other class instances: own enumerable properties,
      // the same view JSON.stringify gives the database sink.
      result = scrubObject(value);
    }
    ancestors.delete(value);
    return result;
  }

  function scrubObject(value: object): Record<string, unknown> {
    return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined).map(([key, entry]) => {
      // A boolean or null can't hold a credential (`hasDeviceKey: true`); a
      // number can (a PIN), so numbers under credential keys are redacted.
      const keep = typeof entry === 'boolean' || entry === null;
      return [key, !keep && isSensitiveKey(key) ? '[redacted]' : scrub(entry)];
    }));
  }

  return scrub(metadata);
}
